/**
 * Where a settings link lands. Links already out in the world name the old
 * layout (Financials › Invoicing › Options, People › Users, legacy ids such as
 * `billing` or `workflow`); each maps to the section and tab that hold that
 * content now. New links simply name the section and tab.
 */
import { isInvoicingDeepLink, resolveInvoicingSection } from '../../modals/invoicingSections';
import { isCustomizationTopic } from '../../modals/accountModal/accountTabs';
import type { SettingsDeviationsArea } from '../../../services/adminApi/policyDeviations';
import { ACCOUNT_SECTIONS } from './accountSections';
import { LOCATION_SECTIONS } from './locationSections';

export interface SettingsDestination {
  section: string;
  /** A tab inside the section, when the link names one. */
  tab?: string;
  /** Location customizations only: the Topic filter (undefined = all topics). */
  topic?: SettingsDeviationsArea;
}

export interface SettingsLinkInput {
  tab?: string | null;
  subTab?: string | null;
  /** Account only: the old third level (account_section), e.g. an Invoicing tab. */
  section?: string | null;
}

const ACCOUNT_IDS = new Set(ACCOUNT_SECTIONS.map((s) => s.id));
const LOCATION_IDS = new Set(LOCATION_SECTIONS.map((s) => s.id));

const ACCOUNT_ALIASES: Record<string, SettingsDestination> = {
  users: { section: 'people', tab: 'users' },
  roles: { section: 'people', tab: 'roles' },
  integrations: { section: 'accounting' },
  billing: { section: 'bill' },
};

/** The old Financials sub-tabs, now sections (or a section's tab). */
const ACCOUNT_FINANCIALS: Record<string, SettingsDestination> = {
  invoicing: { section: 'invoicing' },
  defaults: { section: 'pricing', tab: 'defaults' },
  discounts: { section: 'pricing', tab: 'discounts' },
  accounting: { section: 'accounting' },
  billing: { section: 'bill' },
};

const clean = (v: string | null | undefined) => (v ? v : undefined);
/** Own keys only: link values come from URLs, and `constructor` must not match Object.prototype. */
const own = <V>(map: Record<string, V>, key: string | undefined): V | undefined =>
  key !== undefined && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;

/** Legacy ids that only renamed a section; normalised before any other rule. */
const ACCOUNT_RENAMES: Record<string, string> = { timesheets: 'time-attendance', policies: 'work-orders' };

export function resolveAccountSettingsLink(input: SettingsLinkInput): SettingsDestination | undefined {
  const raw = clean(input.tab);
  const tab = own(ACCOUNT_RENAMES, raw) ?? raw;
  const subTab = clean(input.subTab);
  const inner = clean(input.section);
  if (!tab) return undefined;

  if (tab === 'financials' || tab === 'invoicing') {
    const target =
      tab === 'invoicing' ? ACCOUNT_FINANCIALS.invoicing : (own(ACCOUNT_FINANCIALS, subTab ?? 'invoicing') ?? ACCOUNT_FINANCIALS.invoicing);
    if (target.section === 'invoicing') {
      // The invoicing tab came from account_section (old) or the sub-tab itself (new links into Invoicing).
      // A deep link (a rule's editor, a contact, Emails needing attention) stays whole.
      const deep = [inner, tab === 'invoicing' ? subTab : undefined].find(isInvoicingDeepLink);
      if (deep) return { section: 'invoicing', tab: deep };
      const t = resolveInvoicingSection(inner) ?? (tab === 'invoicing' ? resolveInvoicingSection(subTab) : undefined);
      return t ? { section: 'invoicing', tab: t } : { section: 'invoicing' };
    }
    return target;
  }
  const alias = own(ACCOUNT_ALIASES, tab);
  if (alias) return alias.tab || !subTab ? alias : { ...alias, tab: subTab };
  if ((tab === 'work-orders' || tab === 'time-attendance') && subTab === 'locations') {
    return { section: 'customizations', topic: tab };
  }
  if (tab === 'customizations') return { section: 'customizations', topic: isCustomizationTopic(subTab) ? subTab : undefined };
  if (!ACCOUNT_IDS.has(tab)) return undefined;
  return subTab ? { section: tab, tab: subTab } : { section: tab };
}

const LOCATION_ALIASES: Record<string, SettingsDestination> = {
  users: { section: 'people' },
  workflow: { section: 'services' },
  ordering: { section: 'services', tab: 'departments' },
  departments: { section: 'services' },
  tags: { section: 'services', tab: 'tags' },
  timesheets: { section: 'time-attendance' },
  restrictions: { section: 'work-orders' },
  pricing: { section: 'pricing', tab: 'pricing' },
};

/** The old location Financials sub-tabs. Financials opened on Accounting. */
const LOCATION_FINANCIALS: Record<string, SettingsDestination> = {
  accounting: { section: 'accounting' },
  defaults: { section: 'pricing', tab: 'defaults' },
  pricing: { section: 'pricing', tab: 'pricing' },
};

export function resolveLocationSettingsLink(input: SettingsLinkInput): SettingsDestination | undefined {
  const tab = clean(input.tab);
  const subTab = clean(input.subTab);
  if (!tab) return undefined;
  if (tab === 'financials') return own(LOCATION_FINANCIALS, subTab ?? 'accounting') ?? LOCATION_FINANCIALS.accounting;
  // `pricing` is both the old Discounts & Fees tab and the new Pricing section:
  // the page always names its tab, so a sub-tab means the new form.
  if (tab === 'pricing' && subTab) return { section: 'pricing', tab: subTab };
  const alias = own(LOCATION_ALIASES, tab);
  if (alias) return alias.tab || !subTab ? alias : { ...alias, tab: subTab };
  if (!LOCATION_IDS.has(tab)) return undefined;
  return subTab ? { section: tab, tab: subTab } : { section: tab };
}
